import { useState } from 'react';
import { TextInput } from '@carbon/react';

interface BufferedNumberInputProps {
  id: string;
  labelText: string;
  hideLabel: boolean;
  initial: string;
  placeholder?: string;
  disabled?: boolean;
  step?: string;
  size?: 'sm' | 'md' | 'lg';
  onCommit: (raw: string) => void;
}

/**
 * A number TextInput that keeps its own raw string buffer so partial input
 * (e.g. a lone "-" for a negative differential) is preserved while typing. The
 * parent commits the raw string and parses it itself. Remount (via React `key`)
 * to reset the buffer when the underlying field identity changes.
 * @param {BufferedNumberInputProps} props - The input props
 * @return {JSX.Element} The buffered number input
 */
export function BufferedNumberInput({
  id,
  labelText,
  hideLabel,
  initial,
  placeholder,
  disabled,
  step = '0.01',
  size = 'sm',
  onCommit,
}: BufferedNumberInputProps): JSX.Element {
  const [raw, setRaw] = useState(initial);
  return (
    <TextInput
      id={id}
      labelText={labelText}
      hideLabel={hideLabel}
      size={size}
      type="number"
      step={step}
      placeholder={placeholder}
      disabled={disabled}
      value={raw}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        setRaw(e.target.value);
        onCommit(e.target.value);
      }}
    />
  );
}

/**
 * Parses a raw numeric string to a number, or '' when blank/invalid.
 * @param {string} raw - The raw input
 * @return {number | ''} The parsed number or empty string
 */
export function parseNumber(raw: string): number | '' {
  if (raw.trim() === '') return '';
  const n = Number(raw);
  return Number.isNaN(n) ? '' : n;
}

/**
 * Parses a raw integer string to an integer, or '' when blank/invalid.
 * Strict — rejects trailing non-numeric chars (unlike `parseInt`) and any
 * non-integer numeric value.
 * @param {string} raw - The raw input
 * @return {number | ''} The parsed integer or empty string
 */
export function parseInteger(raw: string): number | '' {
  if (raw.trim() === '') return '';
  const n = Number(raw);
  return Number.isInteger(n) ? n : '';
}
